<template>
  <v-container>
    <PageHeader subtitle="Manage users and their details." />
    <Subheader v-model="search" title="Users" search="users">
      <v-btn dark color="success" @click="showAddUser=!showAddUser">
        <v-icon>add</v-icon>
        {{ $t('Button.Add_a_new_user') }}
      </v-btn>
    </Subheader>

    <v-card>
      <v-card-text>
        <v-data-table :headers="headers" :items="userList" :pagination.sync="pagination" :search="search">
          <template slot="items" slot-scope="props">
            <td class="text-xs-left">{{ props.item.UserName }}</td>
            <td class="text-xs-left">{{ props.item.Email }}</td>
            <td class="text-xs-left">{{ props.item.UserRole }}</td>
            <td class="text-xs-center">
              <v-icon v-show="props.item.IsSysAdmin" color="success">check</v-icon>
              <v-icon v-show="!props.item.IsSysAdmin" color="delete">close</v-icon>
            </td>
            <td class="text-xs-center">
              <v-icon v-show="props.item.IsTenantAdmin" color="success">check</v-icon>
              <v-icon v-show="!props.item.IsTenantAdmin" color="delete">close</v-icon>
            </td>
            <td class="text-xs-center">
              <v-icon v-show="props.item.IsServiceAccount" color="success">check</v-icon>
              <v-icon v-show="!props.item.IsServiceAccount" color="delete">close</v-icon>
            </td>
            <td class="text-xs-right">
              <v-btn dark small color="teal accent-4" v-if="props.item.IsLockedOut" @click="unlockUser=!unlockUser">
                <v-icon>lock_open</v-icon>
                {{ $t('Button.Unlock') }}
              </v-btn>
              <v-btn small depressed :disabled="areSameName(username, props.item.UserName) || props.item.IsServiceAccount" @click="showUserDetail=!showUserDetail; userDetail=props.item">{{ $t('Button.Edit_Detail') }}</v-btn>
              <v-btn small depressed :disabled=" props.item.IsServiceAccount" @click="showEditPassword=!showEditPassword; userDetail=props.item">{{ $t('Button.Edit_Password') }}</v-btn>
              <v-btn small depressed color="error" :disabled="areSameName(username, props.item.UserName) || props.item.IsServiceAccount" @click="dialog = true; deleteUser = props.item.UserName">
                <v-icon>delete_forever</v-icon>
                {{ $t('Button.Remove') }}
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Remove user</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <p>{{ $t('AdministrationUserManage.confirmTitleMsg', { msg: deleteUser }) }}</p>
          <p>{{ $t('AdministrationUserManage.confirmTextMsg') }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" @click.native="agreeDeleteUser()">
            <v-icon>delete_forever</v-icon>
            {{ $t('Button.Remove') }}
          </v-btn>
          <v-btn color="secondary" outline @click.native="dialog = false">{{ $t('Button.CANCEL') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card color="grey lighten-4">
      <v-snackbar style="text-align:center" :color="snackColor" :timeout="null" top v-model="snackbar">
        {{ snackMessage }}
        <v-btn dark flat @click.native="snackbar = false">{{ $t('Button.Close') }}</v-btn>
      </v-snackbar>
    </v-card>
    <AdministrationUnlockUserDialog :show="unlockUser" />
    <AdministrationEditUserDetailDialog :show="showUserDetail" :userDetail="userDetail" />
    <AdministrationEditUserPasswordDialog :show="showEditPassword" :userDetail="userDetail" />
    <AdministrationAddUserDetailDialog :show="showAddUser" />
  </v-container>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'
  const admin = createNamespacedHelpers('admin')
  const authenticate = createNamespacedHelpers('authenticate')

  import AdministrationEditUserDetailDialog from '@/components/Administration/Users/AdministrationEditUserDetailDialog'
  import AdministrationEditUserPasswordDialog from '@/components/Administration/Users/AdministrationEditUserPasswordDialog'
  import AdministrationAddUserDetailDialog from '@/components/Administration/Users/AdministrationAddUserDetailDialog'
  import AdministrationUnlockUserDialog from '@/components/Administration/Users/AdministrationUnlockUserDialog'
  import AuthenticationUtil from '@/components/mixins/AuthenticationUtil'
  export default {
    components: {
      AdministrationEditUserDetailDialog,
      AdministrationAddUserDetailDialog,
      AdministrationUnlockUserDialog,
      AdministrationEditUserPasswordDialog,
    },
    mixins: [AuthenticationUtil],
    data() {
      return {
        dialog: false,
        snackbar: false,
        snackMessage: '',
        snackColor: '',
        search: '',
        pagination: {
          rowsPerPage: 10,
        },
        headers: [{
          align: 'left',
          text: 'Username',
          value: 'UserName',
        },
        {
          align: 'left',
          text: 'Email',
          value: 'Email',
        },
        {
          align: 'left',
          text: 'Tenant',
          value: 'UserRole',
        },
        {
          align: 'left',
          text: 'Is System Admin',
          value: 'IsSysAdmin',
        },
        {
          align: 'left',
          text: 'Is Tenant Admin',
          value: 'IsTenantAdmin',
        },
        {
          align: 'left',
          text: 'Is Service Account',
          value: 'IsServiceAccount',
        },
        {
          sortable: false,
          text: '',
          value: '',
        },
        ],
        unlockUser: false,
        showUserDetail: false,
        showEditPassword: false,
        showAddUser: false,
        userDetail: {},
        deleteUser: '',
      }
    },
    mounted() {
      if (!this.userList || !this.userList.length) {
        this.getUserList()
      }
    },
    computed: {
      ...admin.mapGetters(['roleList', 'userList']),
      ...authenticate.mapGetters(['username']),
    },
    methods: {
      ...admin.mapActions(['getRoleList', 'getUserList', 'postDeleteUser']),
      agreeDeleteUser() {
        let options = {
          data: {
            UserName: this.deleteUser,
          },
          successCb: () => {
            this.snackMessage = this.$t('AdministrationUserManage.deleteUserSuccessMsg')
            this.snackColor = 'success'
            this.snackbar = true
            this.dialog = false
          },
          failedCb: (err) => {
            this.snackColor = 'error'
            this.snackMessage = err
            this.snackbar = true
          },
        }
        this.postDeleteUser(options)
      },
    },
  }
</script>

<style lang="stylus" scoped>
  .toolbar__title.display-2 {
    padding-bottom: 10px;
    margin-left: 10px !important;
  }
</style>